import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Spinner, Alert, Dropdown, Form, Badge, Row, Col, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { reorderAlertAPI } from '../api/reorderAlertAPI';
import { stockItemCategoryAPI } from '../api/stockItemCategoryAPI';
import { vendorAPI } from '../api/vendorAPI';
import { useSearchWithDebounce } from '../hooks/useSearchWithDebounce';
import { highlightText, formatQuantityWithCases } from '../utils/commonUtils';
import ReorderAlertDetailRow from '../components/ReorderAlertDetailRow';

/**
 * ReorderAlertsWidget - Display reorder alerts based on projected inventory levels
 *
 * Features:
 * - Severity-based alerts (Critical, Warning, Watch)
 * - Filters by severity, category, and manufacturer
 * - Search functionality
 * - Expandable rows showing detailed analysis
 */
const ReorderAlertsWidget = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [summary, setSummary] = useState({ critical_count: 0, warning_count: 0, watch_count: 0 });

    // Filters (severity is always 'critical')
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedManufacturer, setSelectedManufacturer] = useState('');

    // Dropdown data
    const [categories, setCategories] = useState([]);
    const [manufacturers, setManufacturers] = useState([]);

    // Expandable rows
    const [expandedRowId, setExpandedRowId] = useState(null);

    // Search
    const { searchTerm, searchInputValue, handleSearchInputChange, clearSearch } = useSearchWithDebounce('');


    // Fetch categories and manufacturers on mount
    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const [categoriesData, manufacturersData] = await Promise.all([
                    stockItemCategoryAPI.getAll(),
                    vendorAPI.getAll()
                ]);
                setCategories(categoriesData || []);
                setManufacturers(manufacturersData || []);
            } catch (err) {
                console.error('Error fetching dropdown data:', err);
            }
        };
        fetchDropdownData();
    }, []);

    // Fetch alerts (always filter by critical severity)
    const fetchAlerts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const filters = {
                severity: 'critical'  // Always show only critical alerts
            };
            if (selectedCategory) filters.category = selectedCategory;
            if (selectedManufacturer) filters.manufacturer = selectedManufacturer;

            const result = await reorderAlertAPI.getReorderAlerts(filters);
            setAlerts(result.warnings || []);
            setSummary(result.summary || { critical_count: 0, warning_count: 0, watch_count: 0 });
        } catch (err) {
            console.error('Error fetching reorder alerts:', err);
            setError(err.response?.data?.error || 'Failed to load reorder alerts');
        } finally {
            setLoading(false);
        }
    }, [selectedCategory, selectedManufacturer]);

    // Fetch on mount and when filters change
    useEffect(() => {
        fetchAlerts();
    }, [fetchAlerts]);

    // Filter alerts by search term (client-side)
    const filteredAlerts = useMemo(() => {
        if (!searchTerm) return alerts;

        const term = searchTerm.toLowerCase();
        return alerts.filter(alert =>
            alert.gtin?.toLowerCase().includes(term) ||
            alert.description?.toLowerCase().includes(term) ||
            alert.manufacturer_name?.toLowerCase().includes(term)
        );
    }, [alerts, searchTerm]);

    // Format number with commas
    const formatNumber = (num) => {
        return Math.round(num).toLocaleString();
    };

    // Helper to get case_pack from alert details
    const getCasePack = (alert) => {
        return alert.details?.stock_item?.case_pack || 1;
    };

    // Handle row click for expanding
    const handleRowClick = (stockItemId) => {
        setExpandedRowId(expandedRowId === stockItemId ? null : stockItemId);
    };

    // Helper component for column header with tooltip
    const ColumnHeaderWithTooltip = ({ title, tooltip, style }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{tooltip}</Tooltip>}
        >
            <th style={{ ...style, cursor: 'help', whiteSpace: 'nowrap' }}>
                {title} <i className="material-icons" style={{ fontSize: '14px', verticalAlign: 'middle', opacity: 0.6 }}>info</i>
            </th>
        </OverlayTrigger>
    );

    return (
        <Card className="shadow-sm h-100">
            <Card.Header>
                <Row className="align-items-center">
                    <Col md={6}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center gap-2">
                                <i className="material-icons">notification_important</i>
                                <strong>Reorder Alerts</strong>
                            </div>
                            <div className="d-flex gap-2">
                                <Badge bg="danger">{summary.critical_count} Critical</Badge>
                            </div>
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="d-flex gap-2 justify-content-end">
                            {/* Search */}
                            <div style={{ position: 'relative', width: '200px' }}>
                                <Form.Control
                                    type="text"
                                    placeholder="Search..."
                                    value={searchInputValue}
                                    onChange={handleSearchInputChange}
                                    size="sm"
                                />
                                {searchInputValue && (
                                    <button
                                        onClick={clearSearch}
                                        style={{
                                            position: 'absolute',
                                            right: '5px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '0 5px'
                                        }}
                                    >
                                        <i className="material-icons" style={{ fontSize: '18px', color: '#6c757d' }}>close</i>
                                    </button>
                                )}
                            </div>

                            {/* Category Filter */}
                            <Dropdown>
                                <Dropdown.Toggle variant="light" size="sm">
                                    {categories.find(c => c._id === selectedCategory)?.name || 'All Categories'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <Dropdown.Item
                                        onClick={() => setSelectedCategory('')}
                                        active={selectedCategory === ''}
                                    >
                                        All Categories
                                    </Dropdown.Item>
                                    {categories.map(category => (
                                        <Dropdown.Item
                                            key={category._id}
                                            onClick={() => setSelectedCategory(category._id)}
                                            active={selectedCategory === category._id}
                                        >
                                            {category.name}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>

                            {/* Manufacturer Filter */}
                            <Dropdown>
                                <Dropdown.Toggle variant="light" size="sm">
                                    {manufacturers.find(m => m._id === selectedManufacturer)?.name || 'All Manufacturers'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <Dropdown.Item
                                        onClick={() => setSelectedManufacturer('')}
                                        active={selectedManufacturer === ''}
                                    >
                                        All Manufacturers
                                    </Dropdown.Item>
                                    {manufacturers.map(manufacturer => (
                                        <Dropdown.Item
                                            key={manufacturer._id}
                                            onClick={() => setSelectedManufacturer(manufacturer._id)}
                                            active={selectedManufacturer === manufacturer._id}
                                        >
                                            {manufacturer.name}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>

                            {/* Refresh Button */}
                            <button
                                onClick={fetchAlerts}
                                disabled={loading}
                                title="Refresh"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    padding: '4px'
                                }}
                            >
                                <i className="material-icons" style={{ fontSize: '20px', color: '#6c757d' }}>
                                    {loading ? 'hourglass_empty' : 'refresh'}
                                </i>
                            </button>
                        </div>
                    </Col>
                </Row>
            </Card.Header>

            <Card.Body className="p-0">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="text-muted mt-2">Loading reorder alerts...</p>
                    </div>
                ) : error ? (
                    <Alert variant="danger" className="m-3">
                        <i className="material-icons" style={{ fontSize: '18px', verticalAlign: 'middle' }}>error</i>
                        {' '}{error}
                    </Alert>
                ) : filteredAlerts.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                        <Table striped hover className="mb-0" style={{ fontSize: '0.9em' }}>
                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                <tr>
                                    <th style={{ minWidth: '120px' }}>GTIN</th>
                                    <th style={{ minWidth: '250px' }}>Description</th>
                                    <th style={{ minWidth: '150px' }}>Manufacturer</th>
                                    <ColumnHeaderWithTooltip
                                        title="Current"
                                        tooltip="Current inventory quantity on hand (shown in cases for items with case pack > 1)"
                                        style={{ minWidth: '150px', textAlign: 'right' }}
                                    />
                                    <ColumnHeaderWithTooltip
                                        title="Arriving"
                                        tooltip="Quantity expected from confirmed purchase orders (shown in cases for items with case pack > 1)"
                                        style={{ minWidth: '150px', textAlign: 'right' }}
                                    />
                                    <ColumnHeaderWithTooltip
                                        title="Daily Usage"
                                        tooltip="Weighted average daily usage rate (shown in cases for items with case pack > 1)"
                                        style={{ minWidth: '180px', textAlign: 'right' }}
                                    />
                                    <ColumnHeaderWithTooltip
                                        title="Lead Time"
                                        tooltip="Vendor shipping lead time in days"
                                        style={{ minWidth: '90px', textAlign: 'right' }}
                                    />
                                    <ColumnHeaderWithTooltip
                                        title="Projected"
                                        tooltip="Projected stock level after lead time: Current + Arriving - (Daily Usage × Lead Time)"
                                        style={{ minWidth: '150px', textAlign: 'right' }}
                                    />
                                    <ColumnHeaderWithTooltip
                                        title="Critical Level"
                                        tooltip="Minimum stock level to maintain: Daily Usage × Lead Time × 1.5 safety factor"
                                        style={{ minWidth: '150px', textAlign: 'right' }}
                                    />
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAlerts.map((alert) => (
                                    <React.Fragment key={alert.stock_item_id}>
                                        <tr
                                            onClick={() => handleRowClick(alert.stock_item_id)}
                                            style={{ cursor: 'pointer' }}
                                            className={expandedRowId === alert.stock_item_id ? 'table-active' : ''}
                                        >
                                            <td>{highlightText(alert.gtin, searchTerm)}</td>
                                            <td>{highlightText(alert.description, searchTerm)}</td>
                                            <td>{highlightText(alert.manufacturer_name, searchTerm)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatQuantityWithCases(alert.current_inventory, getCasePack(alert))}</td>
                                            <td style={{ textAlign: 'right' }}>{formatQuantityWithCases(alert.arriving_shipments, getCasePack(alert))}</td>
                                            <td style={{ textAlign: 'right' }}>{formatQuantityWithCases(alert.daily_usage, getCasePack(alert), { isRate: true })}</td>
                                            <td style={{ textAlign: 'right' }}>
                                                {alert.lead_time_days} days{alert.lead_time_is_default && <span style={{ color: '#0d6efd' }}>*</span>}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <span className={`fw-bold text-${alert.projected_stock <= 0 ? 'danger' : alert.projected_stock < alert.critical_stock_level ? 'warning' : 'info'}`}>
                                                    {formatQuantityWithCases(alert.projected_stock, getCasePack(alert))}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {formatQuantityWithCases(alert.critical_stock_level, getCasePack(alert))}{alert.critical_level_is_dynamic && <span style={{ color: '#0d6efd' }}>*</span>}
                                            </td>
                                        </tr>
                                        {expandedRowId === alert.stock_item_id && (
                                            <tr>
                                                <td colSpan="9" className="p-0">
                                                    <ReorderAlertDetailRow alert={alert} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ) : (
                    <Alert variant="success" className="m-3">
                        <i className="material-icons" style={{ fontSize: '18px', verticalAlign: 'middle' }}>check_circle</i>
                        {' '}No reorder alerts found. All stock levels are healthy!
                    </Alert>
                )}
            </Card.Body>
        </Card>
    );
};

export default ReorderAlertsWidget;
